import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Lock,
  Eye,
  EyeOff,
  Shield,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";

const Step6 = ({
  formData,
  setFormData,
  onNext,
  onPrev,
  isSubmitting = false,
  submitError = null,
}) => {
  const [password, setPassword] = useState(formData.password || "");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Sync password with formData when formData changes (e.g., when navigating back)
  useEffect(() => {
    if (formData.password) {
      setPassword(formData.password);
    }
  }, [formData.password]);

  const passwordRequirements = useMemo(() => [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { text: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { text: "Contains number", met: /\d/.test(password) },
    {
      text: "Contains special character",
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ], [password]);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      long: password.length >= 12,
    };

    // Calculate score
    Object.values(checks).forEach((check) => {
      if (check) score++;
    });

    // Additional points for length
    if (password.length >= 16) score += 2;
    else if (password.length >= 12) score += 1;

    // Determine strength level
    if (score <= 2) return { strength: 1, label: "Weak", color: "bg-red-500" };
    if (score <= 4)
      return { strength: 2, label: "Fair", color: "bg-orange-500" };
    if (score <= 6)
      return { strength: 3, label: "Good", color: "bg-yellow-500" };
    if (score <= 8)
      return { strength: 4, label: "Strong", color: "bg-green-500" };
    return { strength: 5, label: "Very Strong", color: "bg-green-600" };
  };

  const passwordStrength = useMemo(() => calculatePasswordStrength(password), [password]);

  const validateForm = () => {
    const newErrors = {};

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (passwordRequirements.some((req) => !req.met)) {
      newErrors.password = "Password does not meet all requirements";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setFormData((prev) => ({ ...prev, password }));
      onNext();
    }
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    setFormData((prev) => ({ ...prev, password: value }));
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const allRequirementsMet = useMemo(() => 
    passwordRequirements.every((req) => req.met), 
    [passwordRequirements]
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className={`py-4 mx-auto w-full max-w-6xl transition-all duration-300 ${
        isSubmitting ? "opacity-90 scale-[0.98]" : "opacity-100 scale-100"
      }`}
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          F. ACCOUNT SECURITY
          <span className="ml-2 text-green-600">(FINAL STEP)</span>
        </h2>
        <p className="text-gray-600">
          Create a strong password to secure your affiliate account
        </p>
      </div>

      <form onSubmit={handleSubmit} className="relative space-y-4">
        {/* Submit Error Display */}
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-red-50 rounded-lg border border-red-200"
          >
            <p className="text-sm text-red-600">{submitError}</p>
          </motion.div>
        )}

        {/* Loading Overlay */}
        {isSubmitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex absolute inset-0 z-10 justify-center items-center bg-white bg-opacity-75 rounded-lg"
          >
            <div className="flex flex-col items-center space-y-3">
              <div className="w-8 h-8 rounded-full border-4 border-red-200 animate-spin border-t-red-600"></div>
              <p className="text-sm font-medium text-gray-700">
                Creating your account...
              </p>
            </div>
          </motion.div>
        )}

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              className={`w-full pl-10 pr-12 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                errors.password ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500"
            >
              {errors.password}
            </motion.p>
          )}
        </div>

        {/* Password Strength Indicator */}
        {password && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-semibold text-gray-800">
                Password Strength
              </span>
              <span
                className={`text-sm font-bold px-2 py-1 rounded-full ${
                  passwordStrength.strength <= 2
                    ? "text-red-700 bg-red-100"
                    : passwordStrength.strength <= 3
                    ? "text-orange-700 bg-orange-100"
                    : passwordStrength.strength <= 4
                    ? "text-yellow-700 bg-yellow-100"
                    : "text-green-700 bg-green-100"
                }`}
              >
                {passwordStrength.label}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="relative mb-3">
              <div className="overflow-hidden h-3 bg-gray-200 rounded-full">
                <div
                  className={`h-full transition-all duration-500 ease-out ${
                    passwordStrength.strength <= 2
                      ? "bg-red-500"
                      : passwordStrength.strength <= 3
                      ? "bg-orange-500"
                      : passwordStrength.strength <= 4
                      ? "bg-yellow-500"
                      : "bg-green-500"
                  }`}
                  style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>Weak</span>
                <span>Very Strong</span>
              </div>
            </div>

            {/* Strength Tips */}
            <div className="text-xs text-gray-600">
              {passwordStrength.strength <= 2 && (
                <div className="flex gap-2 items-center">
                  <div className="w-1 h-1 bg-red-500 rounded-full"></div>
                  <span>
                    Add more characters, numbers, and special characters
                  </span>
                </div>
              )}
              {passwordStrength.strength === 3 && (
                <div className="flex gap-2 items-center">
                  <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                  <span>Good start! Consider adding more complexity</span>
                </div>
              )}
              {passwordStrength.strength === 4 && (
                <div className="flex gap-2 items-center">
                  <div className="w-1 h-1 bg-yellow-500 rounded-full"></div>
                  <span>Strong password! You're almost there</span>
                </div>
              )}
              {passwordStrength.strength === 5 && (
                <div className="flex gap-2 items-center">
                  <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                  <span>Excellent! Your password is very secure</span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Password Requirements */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ delay: 0.3 }}
          className={`p-4 space-y-2 rounded-lg border transition-all duration-300 ${
            isSubmitting
              ? "bg-red-50 border-red-200 animate-pulse"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <h4 className="mb-2 text-sm font-medium text-gray-700">
            Password Requirements:
          </h4>
          {passwordRequirements.map((requirement, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="flex items-center space-x-2"
            >
              <CheckCircle
                className={`w-4 h-4 ${
                  requirement.met ? "text-green-500" : "text-gray-400"
                }`}
              />
              <span
                className={`text-sm ${
                  requirement.met ? "text-green-600" : "text-gray-600"
                }`}
              >
                {requirement.text}
              </span>
            </motion.div>
          ))}
        </motion.div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              className={`w-full pl-10 pr-12 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                errors.confirmPassword ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500"
            >
              {errors.confirmPassword}
            </motion.p>
          )}
        </div>

        {/* Security Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="p-4 bg-blue-50 rounded-lg border border-blue-200"
        >
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <h4 className="mb-1 text-sm font-medium text-blue-800">
                Security Tips
              </h4>
              <p className="text-sm text-blue-700">
                Your password is encrypted and stored securely. Never share your
                password with anyone.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <motion.button
            type="button"
            onClick={onPrev}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </motion.button>

          <motion.button
            type="submit"
            whileHover={!isSubmitting ? { scale: 1.02 } : {}}
            whileTap={!isSubmitting ? { scale: 0.98 } : {}}
            disabled={
              !allRequirementsMet ||
              password !== confirmPassword ||
              isSubmitting
            }
            className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl ${
              allRequirementsMet &&
              password === confirmPassword &&
              !isSubmitting
                ? "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-500/25 hover:shadow-red-500/40"
                : isSubmitting
                ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-500/25 animate-pulse"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <div className="flex justify-center items-center">
                <svg
                  className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating Account...
              </div>
            ) : (
              "Complete Registration"
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default Step6;
